import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { EzPanelComponent } from '@template-ui/components/ez-panel/panel/panel.component';
import { visibleGeneratedFields } from '@template-ui/helpers/generated-form.helpers';
import {
  captureAnswerLabel,
  captureFormDefinition,
  WorkflowCaptureField,
  WorkflowFormSubmission,
} from '../workflow-commands/workflow-capture-form';
import { captureLookupAnswerLabel } from '../workflow-commands/workflow-form-lookup';

/** Read-only projection of answers and the schema retained by their publication. */
@Component({
  selector: 'phms-workflow-form-submissions',
  standalone: true,
  imports: [CommonModule, EzPanelComponent],
  template: `<ui-panel
    *ngIf="submissions.length"
    panelTitle="Submitted forms"
    aria-label="Submitted forms"
  >
    <div class="forms">
      <details
        *ngFor="let submission of submissions; trackBy: trackSubmission"
        [open]="
          submission.occurrenceId === selectedOccurrenceId ||
          submissions.length === 1
        "
      >
        <summary>
          <strong>{{ submission.form.title }}</strong
          ><span
            >{{ submission.stepLabel || 'Completed work'
            }}<ng-container *ngIf="submission.occurrenceNumber">
              · Visit {{ submission.occurrenceNumber }}</ng-container
            >
            · {{ submission.submittedUtc | date: 'medium' }}</span
          >
        </summary>
        <p
          *ngIf="submission.masked || submission.values === null"
          role="status"
        >
          These answers are protected and are not available in this view.
        </p>
        <dl *ngIf="!submission.masked && submission.values !== null">
          <div *ngFor="let field of fields(submission)">
            <dt>{{ field.label }}</dt>
            <dd>{{ answer(field, submission.values[field.id], submission) }}</dd>
          </div>
        </dl>
      </details>
    </div>
  </ui-panel>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
      }
      .forms {
        padding: 0.25rem 1rem;
      }
      details + details {
        border-top: 1px solid var(--border-color, #d4dce4);
      }
      summary {
        cursor: pointer;
        padding: 0.75rem 0;
      }
      summary strong {
        margin-right: 0.75rem;
      }
      summary span {
        font-size: 0.8rem;
        color: var(--custom-text-secondary-color, #596577);
      }
      dl {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(16rem, 100%), 1fr));
        gap: 0.85rem 1.25rem;
        margin: 0 0 1rem;
      }
      dl div {
        min-width: 0;
      }
      dt {
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--custom-text-secondary-color, #596577);
      }
      dd {
        margin: 0.2rem 0 0;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
      p {
        margin: 0 0 1rem;
      }
    `,
  ],
})
export class WorkflowFormSubmissionsComponent {
  @Input() submissions: WorkflowFormSubmission[] = [];
  @Input() selectedOccurrenceId: number | null = null;
  answer(field: WorkflowCaptureField, value: unknown, submission: WorkflowFormSubmission): string {
    if (submission.masked) return 'Not provided';
    return field.lookup ? captureLookupAnswerLabel(field, value, submission.lookupEvidence?.[field.id]) : captureAnswerLabel(field, value);
  }
  trackSubmission(_: number, submission: WorkflowFormSubmission): number {
    return submission.id;
  }
  fields(submission: WorkflowFormSubmission): WorkflowCaptureField[] {
    const definition = captureFormDefinition(submission.form);
    if (!definition || submission.masked || !submission.values) return [];
    const visible = new Set(
      visibleGeneratedFields(definition, submission.values).map(
        (field) => field.name,
      ),
    );
    return submission.form.fields.filter((field) => visible.has(field.id));
  }
}
